import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { 
  BarChart3, TrendingUp, TrendingDown, Target, Clock, 
  CheckCircle, XCircle, AlertTriangle, Zap, Award,
  ChevronRight, Calendar, Brain, ArrowUp, ArrowDown, Minus
} from "lucide-react";

const UniversalAnalyticsDashboard = ({ onBack, onPractice }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [weakTopics, setWeakTopics] = useState([]);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      // Load all data in parallel
      const [dashRes, weakRes, progressRes] = await Promise.all([
        axios.get(`${API_BASE}/universal-analytics/dashboard`, { headers }),
        axios.get(`${API_BASE}/universal-analytics/weak-topics`, { headers }),
        axios.get(`${API_BASE}/universal-analytics/progress?days=30`, { headers })
      ]);
      
      if (dashRes.data.has_data) {
        setDashboard(dashRes.data.dashboard);
      }
      setWeakTopics(weakRes.data.weak_topics || []);
      if (progressRes.data.has_data) {
        setProgress(progressRes.data);
      }
    } catch (err) {
      toast({ variant: "destructive", description: "Could not load analytics" });
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (direction) => {
    if (direction === "improving") return <TrendingUp className="w-4 h-4 text-emerald-500" />;
    if (direction === "declining") return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 80) return "text-emerald-600 bg-emerald-50";
    if (accuracy >= 60) return "text-amber-600 bg-amber-50";
    return "text-red-600 bg-red-50";
  };

  const getSeverityColor = (severity) => {
    if (severity === "high") return "bg-red-100 text-red-800 border-red-200";
    if (severity === "medium") return "bg-amber-100 text-amber-800 border-amber-200";
    return "bg-blue-100 text-blue-800 border-blue-200";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
          <p className="text-lg font-semibold text-slate-900">Loading Analytics</p>
          <p className="text-sm text-slate-500">Crunching your performance data...</p>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <Button variant="outline" onClick={onBack} className="mb-6">
            ← Back
          </Button>
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">No Analytics Yet</h2>
              <p className="text-slate-500 mb-6">
                Start practicing to see your performance analytics!
              </p>
              <Button onClick={() => onPractice && onPractice()} className="bg-indigo-600 hover:bg-indigo-700">
                <Target className="w-4 h-4 mr-2" />
                Start Practice
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const { overview, strongest_topics, weakest_topics, difficulty_performance, trend, recent_history } = dashboard;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={onBack}>
            ← Back
          </Button>
          <Badge variant="outline" className="px-4 py-2 text-sm border-indigo-500 text-indigo-600">
            <BarChart3 className="w-3 h-3 mr-1" />
            Performance Analytics
          </Badge>
        </div>

        {/* Title */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Award className="w-7 h-7 text-indigo-600" />
            Your Performance Dashboard
          </h1>
          <p className="text-slate-500 mt-1">
            Track your progress, identify weak areas, and improve strategically
          </p>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-slate-500 uppercase tracking-wide">Tests Taken</span>
                <Target className="w-4 h-4 text-indigo-500" />
              </div>
              <p className="text-3xl font-bold text-slate-900">{overview.total_tests}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-slate-500 uppercase tracking-wide">Questions</span>
                <Brain className="w-4 h-4 text-purple-500" />
              </div>
              <p className="text-3xl font-bold text-slate-900">{overview.total_questions}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-slate-500 uppercase tracking-wide">Accuracy</span>
                <CheckCircle className="w-4 h-4 text-emerald-500" />
              </div>
              <p className={`text-3xl font-bold ${overview.overall_accuracy >= 70 ? 'text-emerald-600' : overview.overall_accuracy >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                {overview.overall_accuracy}%
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-slate-500 uppercase tracking-wide">Avg Time</span>
                <Clock className="w-4 h-4 text-blue-500" />
              </div>
              <p className="text-3xl font-bold text-slate-900">{overview.avg_time_per_question}s</p>
            </CardContent>
          </Card>
        </div>

        {/* Trend & Score Breakdown */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Trend Card */}
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                {getTrendIcon(trend.direction)}
                Performance Trend
              </h3>
              <div className="flex items-center gap-4">
                <div className={`text-4xl font-bold ${trend.direction === 'improving' ? 'text-emerald-600' : trend.direction === 'declining' ? 'text-red-600' : 'text-slate-600'}`}>
                  {trend.change > 0 ? '+' : ''}{trend.change}%
                </div>
                <div className="text-sm text-slate-500">
                  {trend.direction === 'improving' && "You're improving! Keep it up!"}
                  {trend.direction === 'declining' && "Accuracy dropping. Focus on weak topics."}
                  {trend.direction === 'stable' && "Consistent performance. Push harder!"}
                </div>
              </div>
              
              {/* Mini chart visualization */}
              <div className="mt-4 flex items-end gap-1 h-16">
                {trend.recent_scores.map((score, idx) => (
                  <div
                    key={idx}
                    className={`flex-1 rounded-t transition-all ${score >= 70 ? 'bg-emerald-400' : score >= 50 ? 'bg-amber-400' : 'bg-red-400'}`}
                    style={{ height: `${Math.max(10, score)}%` }}
                    title={`${score}%`}
                  />
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-2">Last {trend.recent_scores.length} tests</p>
            </CardContent>
          </Card>

          {/* Score Breakdown */}
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-indigo-500" />
                Score Breakdown
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    Correct
                  </span>
                  <span className="font-semibold text-emerald-600">{overview.total_correct}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600 flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-500" />
                    Wrong
                  </span>
                  <span className="font-semibold text-red-600">{overview.total_wrong}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600 flex items-center gap-2">
                    <Minus className="w-4 h-4 text-slate-400" />
                    Skipped
                  </span>
                  <span className="font-semibold text-slate-600">{overview.total_skipped}</span>
                </div>
                
                {/* Progress bar */}
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden flex">
                  <div 
                    className="bg-emerald-500 transition-all"
                    style={{ width: `${(overview.total_correct / overview.total_questions) * 100}%` }}
                  />
                  <div 
                    className="bg-red-500 transition-all"
                    style={{ width: `${(overview.total_wrong / overview.total_questions) * 100}%` }}
                  />
                  <div 
                    className="bg-slate-300 transition-all"
                    style={{ width: `${(overview.total_skipped / overview.total_questions) * 100}%` }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Weak Topics Alert */}
        {weakTopics.length > 0 && (
          <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="p-6">
              <h3 className="font-semibold text-amber-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Topics Needing Attention ({weakTopics.length})
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {weakTopics.slice(0, 6).map((topic, idx) => (
                  <div 
                    key={idx}
                    className={`p-3 rounded-lg border ${getSeverityColor(topic.severity)} cursor-pointer hover:shadow-md transition-all`}
                    onClick={() => onPractice && onPractice(topic.topic)}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">{topic.topic_name}</span>
                      <Badge variant="outline" className="text-xs">
                        {topic.accuracy}%
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="opacity-75">{topic.attempts} attempts</span>
                      <span className="flex items-center gap-1">
                        Practice <ChevronRight className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Strongest & Weakest Topics */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Strongest */}
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
                Strongest Topics
              </h3>
              <div className="space-y-3">
                {strongest_topics.map((topic, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                    <span className="font-medium text-slate-800">{topic.topic_name}</span>
                    <Badge className="bg-emerald-100 text-emerald-800">
                      {topic.accuracy}%
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Difficulty Performance */}
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-purple-500" />
                By Difficulty
              </h3>
              <div className="space-y-3">
                {difficulty_performance.map((diff, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 capitalize">{diff.difficulty}</span>
                    <div className="flex items-center gap-3">
                      <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${diff.accuracy >= 70 ? 'bg-emerald-500' : diff.accuracy >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                          style={{ width: `${diff.accuracy}%` }}
                        />
                      </div>
                      <span className="text-sm font-semibold w-12 text-right">{diff.accuracy}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent History */}
        {recent_history && recent_history.length > 0 && (
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-500" />
                Recent Practice Sessions
              </h3>
              <div className="space-y-2">
                {recent_history.map((session, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <span className="font-medium text-slate-800 capitalize">{session.section}</span>
                      <span className="text-xs text-slate-500 ml-2">({session.exam_type})</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-slate-600">{session.score}/{session.total}</span>
                      <Badge className={getAccuracyColor(session.accuracy)}>
                        {session.accuracy}%
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progress Summary */}
        {progress && (
          <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
            <CardContent className="p-6">
              <h3 className="font-semibold text-indigo-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                30-Day Progress Summary
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-indigo-600">{progress.summary.total_tests}</p>
                  <p className="text-xs text-indigo-500">Tests Completed</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-indigo-600">{progress.summary.total_questions}</p>
                  <p className="text-xs text-indigo-500">Questions Solved</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-indigo-600">{progress.summary.avg_daily_accuracy}%</p>
                  <p className="text-xs text-indigo-500">Avg Accuracy</p>
                </div>
                <div className="text-center">
                  <p className={`text-3xl font-bold ${progress.summary.improvement >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {progress.summary.improvement > 0 ? '+' : ''}{progress.summary.improvement}%
                  </p>
                  <p className="text-xs text-indigo-500">Improvement</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

      </div>
    </div>
  );
};

export default UniversalAnalyticsDashboard;
